import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Search,
  CheckCircle,
  CheckCircle2,
  Package,
  Truck,
  MapPin,
  RotateCcw,
  AlertTriangle,
  Loader2,
  Calendar,
  Hash,
  History,
  ArrowLeft,
  Route as RouteIcon,
} from 'lucide-react';
import Navbar from '@/components/landing/Navbar';
import Footer from '@/components/landing/Footer';
import SEO from '@/components/SEO';
import {
  trackShipment,
  classifyStatus,
  formatEventTime,
  formatDate,
  humanizeStatus,
} from '@/lib/tracking';

const STATUS_STYLES = {
  delivered: { pill: 'bg-green-100 text-green-700', dot: 'bg-green-500', accent: 'from-green-500 to-emerald-600', Icon: CheckCircle },
  ofd:       { pill: 'bg-orange-100 text-orange-700', dot: 'bg-orange-500', accent: 'from-orange-500 to-red-500', Icon: MapPin },
  transit:   { pill: 'bg-orange-100 text-orange-700', dot: 'bg-orange-500', accent: 'from-orange-500 to-orange-600', Icon: Truck },
  pickup:    { pill: 'bg-blue-100 text-blue-700', dot: 'bg-blue-500', accent: 'from-blue-500 to-cyan-600', Icon: Package },
  created:   { pill: 'bg-slate-100 text-slate-700', dot: 'bg-slate-400', accent: 'from-slate-400 to-slate-500', Icon: Package },
  rto:       { pill: 'bg-amber-100 text-amber-700', dot: 'bg-amber-500', accent: 'from-amber-500 to-orange-500', Icon: RotateCcw },
  failed:    { pill: 'bg-red-100 text-red-700', dot: 'bg-red-500', accent: 'from-red-500 to-rose-600', Icon: AlertTriangle },
  neutral:   { pill: 'bg-gray-100 text-gray-700', dot: 'bg-gray-400', accent: 'from-gray-400 to-gray-500', Icon: Package },
};
const getStyle = (b) => STATUS_STYLES[b] || STATUS_STYLES.neutral;

// Color-keyed tile palette; card container styling below matches the homepage's navy design system.
const TILE_PALETTES = {
  orange:  'from-orange-500 to-orange-600',
  amber:   'from-amber-500 to-orange-500',
  rose:    'from-orange-500 to-rose-500',
  red:     'from-red-500 to-orange-500',
  green:   'from-green-500 to-emerald-600',
  slate:   'from-slate-500 to-slate-600',
};

const StatCard = ({ label, value, Icon, palette = 'orange', children }) => (
  <div className="group relative rounded-2xl border border-gray-200 bg-gradient-to-br from-white to-blue-50/30 p-5 hover:border-blue-500 hover:shadow-lg transition-all duration-300 overflow-hidden">
    <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-br from-blue-200/30 to-transparent rounded-bl-full opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
    <div className="relative flex items-start gap-4">
      <div
        className={`flex-shrink-0 w-11 h-11 rounded-xl bg-gradient-to-br ${TILE_PALETTES[palette] || TILE_PALETTES.orange} flex items-center justify-center shadow-md`}
      >
        <Icon className="w-5 h-5 text-white" aria-hidden="true" />
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-[10px] sm:text-xs uppercase tracking-[0.14em] text-gray-500 font-semibold">
          {label}
        </p>
        {children ? (
          <div className="mt-1">{children}</div>
        ) : (
          <p className="mt-1 text-gray-900 font-semibold text-base sm:text-lg break-words">
            {value || '—'}
          </p>
        )}
      </div>
    </div>
  </div>
);

const QUICK_NOTES = [
  'Live milestone updates from pickup to delivery',
  'Support for AWB / tracking numbers from connected courier partners',
  'Estimated delivery date and latest location visibility',
];

const TrackOrder = () => {
  const [awb, setAwb] = useState('');
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const resultRef = useRef(null);
  const abortRef = useRef(null);

  useEffect(() => () => abortRef.current?.abort?.(), []);

  const handleTrack = async () => {
    const value = awb.trim();
    if (!value) {
      setError('Please enter a tracking / AWB number.');
      setResult(null);
      return;
    }
    abortRef.current?.abort?.();
    const controller = new AbortController();
    abortRef.current = controller;

    setIsLoading(true);
    setError('');
    setResult(null);

    try {
      const data = await trackShipment(value, { signal: controller.signal });
      setResult(data);
      requestAnimationFrame(() => {
        resultRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    } catch (err) {
      if (err?.name === 'CanceledError') return;
      setError(err?.userMessage || 'Invalid tracking number or shipment not found.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleBack = () => {
    setResult(null);
    setError('');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const bucket = classifyStatus(result?.status);
  const style = getStyle(bucket);
  const StatusIcon = style.Icon || Package;
  const timelineEvents = useMemo(() => {
    const events = Array.isArray(result?.events) ? [...result.events] : [];
    return events.sort((a, b) => {
      const ta = a?.timestamp ? new Date(a.timestamp).getTime() : Number.NaN;
      const tb = b?.timestamp ? new Date(b.timestamp).getTime() : Number.NaN;
      if (Number.isNaN(ta) && Number.isNaN(tb)) return 0;
      if (Number.isNaN(ta)) return 1;
      if (Number.isNaN(tb)) return -1;
      return tb - ta;
    });
  }, [result?.events]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-50">
      <SEO
        title="Track Your Order"
        description="Track your Boxd Logistics shipments in real time using your AWB number or order ID."
        keywords="track order, shipment tracking, awb tracking, boxd logistics tracking"
        canonical="https://boxdlogistics.in/track-order"
      />
      <Navbar />

      <section className="relative pt-28 pb-20 px-4 overflow-hidden">
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            backgroundImage:
              'linear-gradient(rgba(59, 130, 246, 0.03) 1px, transparent 1px), linear-gradient(90deg, rgba(59, 130, 246, 0.03) 1px, transparent 1px)',
            backgroundSize: '52px 52px',
          }}
        />
        <div className="absolute top-10 right-0 w-72 h-72 rounded-full bg-gradient-to-br from-blue-200/30 to-blue-300/10 blur-3xl pointer-events-none" />
        <div className="absolute bottom-0 left-0 w-72 h-72 rounded-full bg-gradient-to-br from-blue-100/30 to-transparent blur-3xl pointer-events-none" />

        <div className="relative max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-[1.15fr_0.85fr] gap-8 items-start">
            <div>
              <div className="inline-flex items-center gap-2 rounded-full border border-blue-200 bg-blue-50 px-4 py-1.5 text-xs font-semibold uppercase tracking-wider text-blue-900">
                <RouteIcon className="w-3.5 h-3.5" />
                Shipment Tracking
              </div>
              <h1 className="mt-5 text-4xl md:text-5xl font-bold leading-tight text-gray-900">
                Track every shipment
                <span className="block bg-gradient-to-r from-blue-800 to-blue-950 bg-clip-text text-transparent">
                  with real-time updates
                </span>
              </h1>
              <p className="mt-4 text-base md:text-lg text-gray-600 max-w-2xl">
                Enter your AWB or tracking number to view latest movement, expected delivery date, and complete timeline.
              </p>
              <ul className="mt-6 space-y-2.5">
                {QUICK_NOTES.map((note) => (
                  <li key={note} className="flex items-start gap-2.5 text-sm text-gray-700">
                    <CheckCircle2 className="w-4 h-4 mt-0.5 text-blue-700 shrink-0" />
                    {note}
                  </li>
                ))}
              </ul>
            </div>

            <div className="rounded-3xl border border-gray-200 bg-white/95 backdrop-blur p-5 sm:p-6 shadow-[0_25px_60px_-30px_rgba(29, 78, 216,0.4)]">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-blue-700 to-blue-800 flex items-center justify-center shadow-md shadow-blue-700/25">
                  <Search className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-gray-900">Track Shipment</p>
                  <p className="text-xs text-gray-500">Fast and secure lookup</p>
                </div>
              </div>
              <div className="space-y-3">
                <label htmlFor="tracking-awb" className="text-xs font-semibold uppercase tracking-[0.14em] text-gray-500">
                  Tracking / AWB Number
                </label>
                <div className="relative">
                  <Hash className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" aria-hidden="true" />
                  <Input
                    id="tracking-awb"
                    type="text"
                    placeholder="e.g. ORD123456"
                    value={awb}
                    onChange={(e) => setAwb(e.target.value)}
                    className="pl-9 h-12 text-base border-gray-200 focus:border-blue-700 focus:ring-blue-700/20 rounded-xl"
                    onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), handleTrack())}
                    aria-invalid={Boolean(error) || undefined}
                  />
                </div>
                <Button
                  onClick={handleTrack}
                  disabled={isLoading}
                  className="w-full h-12 px-6 bg-gradient-to-r from-blue-800 to-blue-900 hover:from-blue-900 hover:to-blue-950 text-white font-semibold rounded-xl inline-flex items-center justify-center gap-2 disabled:opacity-70"
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />
                      Tracking...
                    </>
                  ) : (
                    <>
                      <Search className="w-4 h-4" aria-hidden="true" />
                      Track Order
                    </>
                  )}
                </Button>
              </div>
              {error && (
                <p role="alert" className="mt-3 inline-flex items-center gap-2 text-sm text-red-600">
                  <AlertTriangle className="w-4 h-4" aria-hidden="true" />
                  {error}
                </p>
              )}
              {!error && !isLoading && (
                <p className="mt-3 text-xs text-gray-500">
                  Tip: You can press Enter after typing your tracking number.
                </p>
              )}
            </div>
          </div>

          {isLoading && (
            <div className="mt-8 rounded-2xl bg-white ring-1 ring-gray-100 p-5 flex items-center gap-3 text-gray-600">
              <Loader2 className="w-5 h-5 animate-spin text-blue-700" aria-hidden="true" />
              <span>Fetching the latest shipment updates...</span>
            </div>
          )}

          {result && !isLoading && (
            <div ref={resultRef} className="mt-10">
              <button
                type="button"
                onClick={handleBack}
                className="inline-flex items-center gap-2 text-sm font-semibold text-gray-700 hover:text-blue-900 bg-white ring-1 ring-gray-200 hover:ring-blue-300 rounded-full px-4 py-2 shadow-sm transition-colors"
              >
                <ArrowLeft className="w-4 h-4" aria-hidden="true" />
                Back to Search
              </button>

              <div className="mt-5 rounded-3xl border border-gray-200 bg-white p-5 md:p-7 shadow-[0_24px_56px_-30px_rgba(15,23,42,0.25)]">
                <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
                  <div>
                    <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-50 text-blue-900 text-xs font-semibold uppercase tracking-wider ring-1 ring-blue-100">
                      <StatusIcon className="w-3.5 h-3.5" />
                      Latest Status
                    </div>
                    <h2 className="mt-3 text-2xl md:text-3xl font-bold text-gray-900">
                      {result.status || 'Tracking In Progress'}
                    </h2>
                    <p className="mt-1 text-sm text-gray-600">
                      Last update from {result.courier || 'courier network'} at {result.currentLocation || 'latest scanned location'}.
                    </p>
                  </div>
                  {result.awb && (
                    <div className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-gray-50 text-gray-700 font-semibold ring-1 ring-gray-200">
                      <Hash className="w-4 h-4" aria-hidden="true" />
                      {result.awb}
                    </div>
                  )}
                </div>

                <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
                  <StatCard
                    label="Order Number"
                    value={result.orderId ? `#${result.orderId}` : '—'}
                    Icon={Package}
                    palette="slate"
                  />
                  <StatCard label="Status" Icon={Truck} palette={bucket === 'delivered' ? 'green' : 'orange'}>
                    <span
                      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold uppercase tracking-wider ${style.pill}`}
                    >
                      <span className={`w-1.5 h-1.5 rounded-full ${style.dot}`} />
                      {result.status || 'Tracking'}
                    </span>
                  </StatCard>
                  <StatCard
                    label="Current Location"
                    value={result.currentLocation || 'Unknown'}
                    Icon={MapPin}
                    palette="rose"
                  />
                  <StatCard
                    label="Courier"
                    value={result.courier || '—'}
                    Icon={Truck}
                    palette="amber"
                  />
                  <StatCard
                    label="Route"
                    value={result.route || '—'}
                    Icon={RouteIcon}
                    palette="orange"
                  />
                  <StatCard
                    label="Expected Delivery"
                    value={result.edd ? formatDate(result.edd) : 'Not available yet'}
                    Icon={Calendar}
                    palette="red"
                  />
                </div>
              </div>

              {timelineEvents.length > 0 && (
                <div className="mt-7 bg-white rounded-3xl ring-1 ring-gray-100 shadow-[0_22px_50px_-30px_rgba(0,0,0,0.25)] p-6 sm:p-8">
                  <div className="flex items-center justify-between gap-3 mb-6">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-700 to-blue-800 flex items-center justify-center shadow-md">
                        <History className="w-5 h-5 text-white" aria-hidden="true" />
                      </div>
                      <h3 className="text-xl font-bold text-gray-900">Tracking Timeline</h3>
                    </div>
                    <span className="text-xs font-semibold uppercase tracking-wider text-gray-500">
                      {timelineEvents.length} updates
                    </span>
                  </div>

                  <ol className="relative pl-10 sm:pl-12 border-l-2 border-dashed border-blue-200 space-y-5">
                    {timelineEvents.map((event, idx) => {
                      const evBucket = classifyStatus(event.statusCode || event.title);
                      const evStyle = getStyle(evBucket);
                      const isLatest = idx === 0;
                      const EvIcon = evStyle.Icon;
                      return (
                        <li key={`${event.title}-${idx}`} className="relative">
                          <span
                            className={`absolute -left-[26px] sm:-left-[30px] top-2 flex items-center justify-center w-7 h-7 rounded-full bg-gradient-to-br ${evStyle.accent} ring-4 ring-white shadow`}
                            aria-hidden="true"
                          >
                            {evBucket === 'delivered' || isLatest ? (
                              <CheckCircle2 className="w-4 h-4 text-white" />
                            ) : (
                              <EvIcon className="w-4 h-4 text-white" />
                            )}
                          </span>
                          <div className="rounded-xl border border-gray-200 bg-white p-4 hover:border-blue-300 transition-colors">
                            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2">
                              <div className="min-w-0">
                                <p className="text-base font-semibold text-gray-900 capitalize">
                                  {humanizeStatus(event.statusCode) || event.title || 'Update'}
                                </p>
                                {event.title &&
                                  humanizeStatus(event.statusCode).toLowerCase() !==
                                    event.title.toLowerCase() && (
                                    <p className="mt-0.5 text-sm text-gray-600 uppercase tracking-wide">
                                      {event.title}
                                    </p>
                                  )}
                              </div>
                              <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-gray-500 sm:text-right">
                                {event.location && (
                                  <span className="inline-flex items-center gap-1 uppercase tracking-wide">
                                    <MapPin className="w-3 h-3 text-blue-700" aria-hidden="true" />
                                    {event.location}
                                  </span>
                                )}
                                {event.timestamp && (
                                  <span className="inline-flex items-center gap-1 uppercase tracking-wide">
                                    <Calendar className="w-3 h-3 text-blue-700" aria-hidden="true" />
                                    {formatEventTime(event.timestamp)}
                                  </span>
                                )}
                              </div>
                            </div>
                            {event.remark && (
                              <p className="mt-2 text-sm text-gray-600">{event.remark}</p>
                            )}
                          </div>
                        </li>
                      );
                    })}
                  </ol>
                </div>
              )}
            </div>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default TrackOrder;
